import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { getCurrentOrganization } from '@/core/state/selectors';
import { SkillCategory } from '@/swagger/constants/contents-orchestrator-enums';
import { KEY_GET_DASHBOARD_CONTENTS, API_GET_DASHBOARD_CONTENTS } from '../constants';
import { MOCK_RESPONSE_GET_DASHBOARD_CONTENTS } from '../mocks';
import {
  API_GET_DASHBOARD_CONTENTS_PROPS,
  API_GET_DASHBOARD_CONTENTS_REQUEST_PATH_PARAMS,
  API_GET_DASHBOARD_CONTENTS_REQUEST_QUERY_PARAMS,
} from '../types/dashboardContents';

export type RequestGetDashboardContents = API_GET_DASHBOARD_CONTENTS_REQUEST_PATH_PARAMS &
  API_GET_DASHBOARD_CONTENTS_REQUEST_QUERY_PARAMS;

export type ResponseGetDashboardContents = API_GET_DASHBOARD_CONTENTS_PROPS['response']['body'];

export type CallParamsGetDashboardContents = Omit<RequestGetDashboardContents, 'organizationId'> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

export type CallResponseGetDashboardContents = ResponseGetDashboardContents;

type CallErrorGetDashboardContents = {
  message: string;
  code: number;
  type: string;
  title: string;
  status: number;
};

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetDashboardContents, 'successSaga' | 'errorSaga'>;
};

type CallSelectorsGetDashboardContents = {
  dashboardVideos?: CallResponseGetDashboardContents['video'];
  dashboardPdfs?: CallResponseGetDashboardContents['pdf'];
  dashboardQuizzes?: CallResponseGetDashboardContents['quiz'];
  fullRequest: FullRequestType;
};

let internalPayload = {};

export const getDashboardContentsCall = createGeneralCall<
  CallParamsGetDashboardContents,
  CallResponseGetDashboardContents,
  CallErrorGetDashboardContents,
  CallSelectorsGetDashboardContents
>({
  id: KEY_GET_DASHBOARD_CONTENTS,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_DASHBOARD_CONTENTS(org as unknown as string, {
        length: 10,
        ...payload,
        skillCategory: SkillCategory.SOFT_SKILL,
      });
    },
  method: 'get',
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  selectors: {
    dashboardVideos: ({ selectors }) =>
      createSelector(selectors.response, res => res?.video || undefined),
    dashboardPdfs: ({ selectors }) =>
      createSelector(selectors.response, res => res?.pdf || undefined),
    dashboardQuizzes: ({ selectors }) =>
      createSelector(selectors.response, res => res?.quiz || undefined),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_DASHBOARD_CONTENTS : undefined,
});
